import { legacyApiAuth, defaultApiBasicX } from '../../apis'
import { IResponseService } from '../charts'
import type {
  IContactData,
  IDownloadResponse,
  IListAvailable,
  IPropertyDetail,
  IPropertyPerformance,
  IResponseProperties,
  IShareResponse,
  TPropertyBy,
  TPropertyEvent,
  TPropertyOtherReason,
  TPropertyReason,
  TPropertyRemoveReason
} from '../../interfaces'

interface IGetProperties {
  page?: number
  limit?: number
  order?: string | null
  orderDirection?: string | null
  filters?: any
}

export const getProperties = async ({
  page = 0,
  limit = 10,
  order = null,
  orderDirection = null,
  filters = null
}: IGetProperties): Promise<IResponseService<IResponseProperties>> => {
  try {
    const response = await legacyApiAuth.get<IResponseProperties>(
      '/v2.0.2/properties/list',
      {
        params: {
          p: page,
          l: limit,
          order,
          orderDirection,
          ...filters
        }
      }
    )
    return {
      isSuccess: true,
      data: response
    }
  } catch (error) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const getPropertyPerformance = async ({
  id
}: {
  id: string
}): Promise<IResponseService<IPropertyPerformance>> => {
  try {
    const response = await legacyApiAuth.get<IPropertyPerformance>(
      `/v2.0.2/property/${id}/performance`
    )
    return {
      isSuccess: true,
      data: response
    }
  } catch (error: any) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const getProperty = async ({
  id
}: {
  id: string
}): Promise<IResponseService<IPropertyDetail>> => {
  try {
    const response = await legacyApiAuth.get<IPropertyDetail>(
      `/v2.0.2/property/${id}`
    )
    return {
      isSuccess: true,
      data: response
    }
  } catch (error) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const getPropertyDetail = async ({
  id,
  search_from
}: {
  id: string
  search_from?: string
}): Promise<IResponseService<IPropertyDetail>> => {
  try {
    const response = await legacyApiAuth.get<IPropertyDetail>(
      `/v2.0.2/property/${id}/view`,
      {
        params: {
          search_from
        }
      }
    )
    return {
      isSuccess: true,
      data: response
    }
  } catch (error) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const postRemovedReason = async ({
  by,
  event,
  otherReason = null,
  propertiesIds,
  reason
}: {
  by: TPropertyBy
  event: TPropertyEvent
  otherReason: TPropertyOtherReason
  propertiesIds: string[]
  reason: TPropertyReason
}) => {
  try {
    await legacyApiAuth.post('/v2.0.2/properties/removed_reason', {
      by,
      event,
      other_reason: otherReason,
      property_id: propertiesIds,
      reason
    })
    return {
      isSuccess: true
    }
  } catch (error) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const archiveProperties = async ({
  propertiesIds,
  removeReason,
  otherReason
}: {
  propertiesIds: string[]
  removeReason?: TPropertyRemoveReason
  otherReason?: string | null
}) => {
  try {
    await legacyApiAuth.post('/v2.0.2/properties/unpublish', {
      property_ids: propertiesIds,
      remove_reason: removeReason,
      other_reason: otherReason
    })
    return {
      isSuccess: true
    }
  } catch (error) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const deleteProperties = async ({
  propertiesIds,
  removeReason,
  otherReason
}: {
  otherReason: string | null
  propertiesIds: string[]
  removeReason: TPropertyRemoveReason
}) => {
  try {
    await legacyApiAuth.post('/v2.0.2/properties/delete', {
      other_reason: otherReason,
      property_ids: propertiesIds,
      remove_reason: removeReason
    })
    return {
      isSuccess: true
    }
  } catch (error) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const shareProperties = async ({
  contactId,
  propertyId,
  email,
  phone,
  name,
  sendEmail,
  requirementId
}: {
  contactId: number | string | undefined
  propertyId: string
  email: string | undefined
  phone: string | undefined
  name: string | undefined
  sendEmail: boolean
  requirementId?: string
}): Promise<IResponseService<IShareResponse>> => {
  try {
    const data = await legacyApiAuth.post<IShareResponse, any>(
      '/v2.0.2/datasheet/share',
      {
        contact_id: contactId,
        property_id: [propertyId],
        email,
        phone,
        name,
        send_email: sendEmail,
        requirement_id: requirementId
      }
    )
    return {
      isSuccess: true,
      data
    }
  } catch (error) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const postPublishProperties = async ({
  propertiesIds,
  portals
}: {
  portals: any
  propertiesIds: string[]
}) => {
  try {
    await legacyApiAuth.post('/v2.0.2/properties/publish', {
      properties_id: propertiesIds,
      ...portals
    })
    return {
      isSuccess: true
    }
  } catch (error) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const postUnPublishProperties = async ({
  propertiesIds,
  associations,
  portalsIds
}: {
  propertiesIds: string[]
  portalsIds: string[] | undefined
  associations: number[]
}) => {
  try {
    await legacyApiAuth.post('/v2.0.2/properties/unpublish_from_portals', {
      property_ids: propertiesIds,
      associations,
      portal_ids: portalsIds
    })
    return {
      isSuccess: true
    }
  } catch (error) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const putRenovateProperties = async ({
  propertiesIds
}: {
  propertiesIds: string[]
}) => {
  try {
    await legacyApiAuth.put('/v2.0.2/properties/touch', {
      property_ids: propertiesIds
    })
    return {
      isSuccess: true
    }
  } catch (error) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const getListAdOptionsAvailable = async ({
  portal
}: {
  portal: string
}): Promise<IResponseService<IListAvailable[]>> => {
  try {
    const response = await legacyApiAuth.get<IListAvailable[]>(
      `/v2.0.2/portals/${portal}/lists_available`
    )
    return {
      isSuccess: true,
      data: response
    }
  } catch (error) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const getContactByName = async ({
  name
}: {
  name: string
}): Promise<IResponseService<IContactData[]>> => {
  try {
    const response = await legacyApiAuth.get<IContactData[]>(
      `/v2.0.2/contacts/search?name=${name}`
    )
    return {
      isSuccess: true,
      data: response
    }
  } catch (error) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const putRetrieveProperty = async ({
  propertyId
}: {
  propertyId: string
}) => {
  try {
    await legacyApiAuth.put(`/v2.0.2/property/${propertyId}/recover`, {})
    return {
      isSuccess: true
    }
  } catch (error) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const getGeneratedPropertiesXlsx = async ({
  statuses
}: {
  statuses: string[]
}) => {
  try {
    const statusesQuery = `statuses=${statuses.join(',')}`
    const response = await defaultApiBasicX.get<IDownloadResponse>(
      `properties/xlsx/properties?${statusesQuery}`
    )

    return {
      isSuccess: true,
      data: response?.data
    }
  } catch (error: any) {
    return {
      isSuccess: false,
      error
    }
  }
}
